import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import IndustryCard from './components/IndustryCard';
import SearchBar from './components/SearchBar';
import OnboardingHeader from './components/OnboardingHeader';
import PopularBadge from './components/PopularBadge';
import { profileService } from '../../services/profileService';
import { useTenant } from '../../contexts/TenantContext';
import { supabase } from '../../lib/supabase';

const IndustrySelection = () => {
  const navigate = useNavigate();
  const { tenantSlug } = useTenant();
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const industries = [
    {
      id: 'technology',
      name: 'Technology & SaaS',
      icon: 'Laptop',
      description: 'Software solutions, cloud services, and enterprise technology sales',
      scenarios: ['Product demos', 'ROI discussions', 'Technical objections'],
      isPopular: true
    },
    {
      id: 'healthcare',
      name: 'Healthcare & Medical',
      icon: 'Heart',
      description: 'Medical devices, pharmaceuticals, and healthcare services',
      scenarios: ['Compliance concerns', 'Clinical evidence', 'Budget constraints'],
      isPopular: true
    },
    {
      id: 'real-estate',
      name: 'Real Estate',
      icon: 'Home',
      description: 'Residential and commercial property sales and leasing',
      scenarios: ['Property tours', 'Pricing negotiations', 'Market conditions'],
      isPopular: true
    },
    {
      id: 'ugc-creator',
      name: 'UGC Content Creator',
      icon: 'Video',
      description: 'Social-first content creators producing UGC for brands across TikTok, IG, and YouTube',
      scenarios: ['Creative briefs', 'Hook/CTA optimization', 'Platform-specific edits'],
      isPopular: true
    },
    {
      id: 'automotive',
      name: 'Automotive',
      icon: 'Car',
      description: 'Vehicle sales, leasing, and automotive services',
      scenarios: ['Test drives', 'Financing options', 'Trade-in values'],
      isPopular: false
    },
    {
      id: 'financial',
      name: 'Financial Services',
      icon: 'DollarSign',
      description: 'Banking, insurance, investment, and financial planning',
      scenarios: ['Risk assessment', 'Portfolio reviews', 'Regulatory compliance'],
      isPopular: true
    },
    {
      id: 'retail',
      name: 'Retail & E-commerce',
      icon: 'ShoppingBag',
      description: 'Consumer goods, online retail, and merchandising',
      scenarios: ['Product recommendations', 'Upselling', 'Return policies'],
      isPopular: false
    },
    {
      id: 'manufacturing',
      name: 'Manufacturing & Industrial',
      icon: 'Factory',
      description: 'Industrial equipment, machinery, and B2B manufacturing',
      scenarios: ['Technical specifications', 'Volume pricing', 'Delivery timelines'],
      isPopular: false
    },
    {
      id: 'consulting',
      name: 'Consulting & Professional Services',
      icon: 'Briefcase',
      description: 'Business consulting, legal services, and professional advisory',
      scenarios: ['Value propositions', 'Case studies', 'Engagement terms'],
      isPopular: false
    },
    {
      id: 'education',
      name: 'Education & Training',
      icon: 'GraduationCap',
      description: 'Educational programs, training services, and learning platforms',
      scenarios: ['Course benefits', 'Certification value', 'Learning outcomes'],
      isPopular: false
    },
    {
      id: 'hospitality',
      name: 'Hospitality & Tourism',
      icon: 'Plane',
      description: 'Hotels, restaurants, travel services, and event planning',
      scenarios: ['Booking inquiries', 'Package deals', 'Amenities discussion'],
      isPopular: false
    }
  ];

  const filteredIndustries = useMemo(() => {
    if (!searchQuery?.trim()) return industries;
    
    const query = searchQuery?.toLowerCase();
    return industries?.filter(
      industry =>
        industry?.name?.toLowerCase()?.includes(query) ||
        industry?.description?.toLowerCase()?.includes(query) ||
        industry?.scenarios?.some(scenario => scenario?.toLowerCase()?.includes(query))
    );
  }, [searchQuery]);

  const popularIndustries = filteredIndustries?.filter(ind => ind?.isPopular);
  const otherIndustries = filteredIndustries?.filter(ind => !ind?.isPopular);

  const handleContinue = async () => {
    if (!selectedIndustry) return;

    setIsSaving(true);
    setError('');

    try {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      // Update user profile with industry
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          industry: selectedIndustry,
          updated_at: new Date().toISOString()
        })
        .eq('id', session.user.id);

      if (updateError) throw updateError;

      // Update auth metadata to track industry
      await supabase.auth.updateUser({
        data: { industry: selectedIndustry }
      });

      // Store in localStorage as fallback
      localStorage.setItem('userIndustry', selectedIndustry);
      localStorage.setItem('hasSeenLumo', 'false'); // Track if user has seen Lumo intro

      // Navigate to Meet Lumo page, preserving tenant
      const tenantQuery = tenantSlug && tenantSlug !== 'default' ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
      navigate(`/app/meet-lumo${tenantQuery}`);
    } catch (err) {
      console.error('Error saving industry:', err);
      setError(err?.message || 'Failed to save industry selection');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    const tenantQuery = tenantSlug && tenantSlug !== 'default' ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    navigate(`/authentication${tenantQuery}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <OnboardingHeader currentStep={1} totalSteps={2} onBack={handleBack} />

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Choose Your Industry
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">
            Select your sales domain to personalize your AI coaching experience with relevant scenarios and objections.
          </p>
        </div>

        <div className="mb-6">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search industries..."
          />
        </div>

        {filteredIndustries?.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg mb-2">No industries found</p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search terms
            </p>
          </div>
        ) : (
          <>
            {popularIndustries?.length > 0 && !searchQuery && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-xl font-semibold text-foreground">Popular Industries</h2>
                  <PopularBadge />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {popularIndustries?.map(industry => (
                    <IndustryCard
                      key={industry?.id}
                      industry={industry}
                      isSelected={selectedIndustry === industry?.id}
                      onSelect={setSelectedIndustry}
                    />
                  ))}
                </div>
              </div>
            )}

            {otherIndustries?.length > 0 && (
              <div className="mb-8">
                {!searchQuery && (
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    All Industries
                  </h2>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {otherIndustries?.map(industry => (
                    <IndustryCard
                      key={industry?.id}
                      industry={industry}
                      isSelected={selectedIndustry === industry?.id}
                      onSelect={setSelectedIndustry}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border pt-6 pb-8 mt-8">
          <div className="max-w-4xl mx-auto px-4">
            {error && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                {error}
              </div>
            )}
            <div className="mb-4">
              <ProgressIndicator
                value={selectedIndustry ? 50 : 0}
                max={100}
                label="Onboarding Progress"
                showPercentage={true}
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                variant="default"
                onClick={handleContinue}
                disabled={!selectedIndustry}
                iconName="ArrowRight"
                iconPosition="right"
                className="flex-1"
              >
                Continue
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-4">
              You can change your industry later in settings
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustrySelection;
